import { useQuery, useQueryClient } from "react-query";
import { genericError } from "./errors";
import { useEffect, useState } from "react";

// Extend Window interface for TypeScript
declare global {
  interface Window {
    __REACT_QUERY_CLIENT__: any;
  }
}

const queryRegistry = new Map<string, string>();

export const refetch = (queryName: string) => {
  const queryKey = queryRegistry.get(queryName);
  if (queryKey) {
    const queryClient = window.__REACT_QUERY_CLIENT__;
    if (queryClient) {
      queryClient.invalidateQueries(JSON.parse(queryKey));
    }
  }
};

export const setQueryClient = (queryClient: any) => {
  window.__REACT_QUERY_CLIENT__ = queryClient;
};

export const hasQuery = (queryName: string): boolean => {
  return queryRegistry.has(queryName);
};

export const getQueryNames = (): string[] => {
  return Array.from(queryRegistry.keys());
};

export const clearQuery = (queryName: string) => {
  const queryKey = queryRegistry.get(queryName);
  if (queryKey) {
    const queryClient = window.__REACT_QUERY_CLIENT__;
    if (queryClient) {
      queryClient.removeQueries(JSON.parse(queryKey));
      queryRegistry.delete(queryName);
    }
  }
};

export const buildPath = (path, params) => {
  if (!params || !Object.keys(params).length) return path;
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) =>
    Array.isArray(v) ? v.forEach((x) => sp.append(`${k}[]`, x)) : sp.append(k, String(v))
  );
  return `${path}?${sp}`;
};

export const LoadingSpinner = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 300);
    return () => clearTimeout(timer);
  }, []);

  if (!show) return null;

  return (
    <div className="d-flex justify-content-center p-5">
      <div className="spinner-grow gray-4 spinner-grow-sm" role="status"></div>
    </div>
  );
};

export const useApiGet = (
  path: string | { [key: string]: string },
  params = {},
  config = {}
) => {
  const queryClient = useQueryClient();
  let queryName;

  if (typeof path === "string") {
    queryName = path;
  } else if (typeof path === "object") {
    queryName = Object.keys(path)[0];
    path = path[queryName];
  }

  useEffect(() => {
    setQueryClient(queryClient);
  }, [queryClient]);

  const queryKey = [path, params];

  if (queryName) {
    queryRegistry.set(queryName, JSON.stringify(queryKey));
  }

  const q = useQuery(
    queryKey,
    () =>
      fetch(buildPath(path, params), {
        headers: { Accept: "application/json" },
      }).then((res) => res.json()),
    {
      keepPreviousData: true,
      ...config,
    }
  );

  let unavailable = null;
  let data = q.data;

  if (q.isFetching && !q.isFetched) {
    unavailable = <LoadingSpinner />;
  } else if (q.error) {
    unavailable = <div>There was an error</div>;
    genericError((q.error as any).message);
  }

  return {
    data,
    unavailable,
    loading: q.isFetching,
    loaded: q.isSuccess,
    refetch: q.refetch,
  };
};

export const usePageData = (params = {}) => {
  return useApiGet(location.pathname + location.search, params);
};

const csrfToken = document
  .querySelector('meta[name="csrf-token"]')
  ?.getAttribute("content");

export const useApiMutation = () => {
  let callback;

  const [saving, setSaving] = useState(false);

  const makeRequest =
    (method: "PUT" | "POST" | "DELETE" | "GET") =>
    async (path: string, data = {}, config = {}) => {
      setSaving(true);
      try {
        let url = path;
        let fetchOptions: any = {
          method,
          ...config,
          headers: {
            ...(method !== "GET" && !(data instanceof FormData)
              ? { "Content-Type": "application/json" }
              : {}),
            "X-CSRF-Token": csrfToken,
            Accept: "application/json",
            ...(config.headers || {}),
          },
        };

        if (method === "GET") {
          // Append data as query params
          const params = new URLSearchParams(data as Record<string, string>).toString();
          if (params) {
            url += (url.includes("?") ? "&" : "?") + params;
          }
        } else {
          if (data instanceof FormData) {
            fetchOptions.body = data;
          } else {
            fetchOptions.body = JSON.stringify(data);
          }
        }

        const response = await fetch(url, fetchOptions);
        const responseData = await response.json();
        callback?.(responseData);
        return { data: responseData };
      } catch (error) {
        genericError();
      } finally {
        setSaving(false);
      }
    };

  const put = makeRequest("PUT");
  const post = makeRequest("POST");
  const destroy = makeRequest("DELETE");
  const get = makeRequest("GET");

  return { post, put, destroy, get, saving };
};

export const onSuccess = (callback) => (response) => {
  if (response.data.success) {
    callback(response.data);
  } else {
    genericError();
  }
};

export const formResponse = (callback, setErrors) => (response) => {
  if (response.data.success) {
    callback(response.data);
    setErrors({});
  } else if (response.data.form_errors) {
    setErrors(response.data.form_errors);
  } else {
    genericError();
  }
};
